import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  collection, getDocs, doc, setDoc, addDoc,
  serverTimestamp, increment,
} from 'firebase/firestore';
import PageHeader from './PageHeader';
import './Catalogue.css';

// Labels des catégories Recipe Studio
const CATEGORIES_MAP = {
  entrees:        'Entrées',
  plats:          'Plats principaux',
  desserts:       'Desserts',
  accompagnements:'Accompagnements',
  boissons:       'Boissons',
  viandes:        'Viandes',
  poissons:       'Poissons',
  vegetarien:     'Végétarien',
  tartes:         'Tartes',
  gateaux:        'Gâteaux',
  glaces:         'Glaces & sorbets',
};

// Résout un ID de catégorie en label lisible.
// Gère les IDs fixes ('plats'), les labels directs déjà résolus ('Plats principaux'),
// et filtre les UUIDs qui n'ont pas encore été re-synchronisés.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const getCatLabel = (id) => {
  if (CATEGORIES_MAP[id]) return CATEGORIES_MAP[id]; // ID fixe connu
  if (UUID_RE.test(id)) return null;                 // UUID non résolu → ignorer
  return id;                                          // Label déjà lisible
};

const formatTime = (min) => {
  if (!min) return null;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h${m.toString().padStart(2, '0')}` : `${h}h`;
};

export default function Catalogue() {
  const navigate = useNavigate();
  const [catalogue, setCatalogue]     = useState([]);
  const [userImports, setUserImports] = useState(new Set());
  const [stats, setStats]             = useState({});
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [selectedCats, setSelectedCats] = useState([]);
  const [selected, setSelected]       = useState(null); // recette ouverte en détail
  const [importing, setImporting]     = useState(false);
  const [importMsg, setImportMsg]     = useState(null); // { text, type }

  // --- Chargement ---
  const load = useCallback(async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    try {
      const [catalogueSnap, importsSnap, statsSnap, userRecipesSnap] = await Promise.all([
        getDocs(collection(db, 'recettesDefaut')),
        getDocs(collection(db, 'catalogueImports', uid, 'imports')),
        getDocs(collection(db, 'catalogueStats')),
        getDocs(collection(db, 'recettesUtilisateur', uid, 'recettes')),
      ]);

      const recipes = catalogueSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      recipes.sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr'));
      setCatalogue(recipes);

      // Vérifier que les recettes importées existent encore dans la collection personnelle
      const existingUserRecipeIds = new Set(userRecipesSnap.docs.map(d => d.id));

      // Fallback pour anciens imports (sans userRecipeId) : correspondance par nom
      const catalogueUserRecipeNames = new Set(
        userRecipesSnap.docs
          .filter(d => d.data().auteur === 'catalogue')
          .map(d => (d.data().nom || '').toLowerCase().trim())
      );
      const catalogueNomById = {};
      catalogueSnap.docs.forEach(d => {
        catalogueNomById[d.id] = (d.data().nom || '').toLowerCase().trim();
      });

      const verifiedImports = new Set(
        importsSnap.docs
          .filter(d => {
            const userRecipeId = d.data().userRecipeId;
            if (userRecipeId) {
              // Nouveau format : vérification directe par ID
              return existingUserRecipeIds.has(userRecipeId);
            }
            // Ancien format : correspondance par nom de la recette catalogue
            const catalogueName = catalogueNomById[d.id];
            return catalogueName ? catalogueUserRecipeNames.has(catalogueName) : false;
          })
          .map(d => d.id)
      );
      setUserImports(verifiedImports);

      const statsMap = {};
      statsSnap.docs.forEach(d => { statsMap[d.id] = d.data().downloadCount || 0; });
      setStats(statsMap);
    } catch (err) {
      console.error('Erreur chargement catalogue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // --- Catégories disponibles (extraites dynamiquement, UUIDs non résolus exclus) ---
  const availableCats = useMemo(() => {
    const ids = new Set();
    catalogue.forEach(r => (r.categories || []).forEach(c => {
      if (getCatLabel(c) !== null) ids.add(c);
    }));
    return [...ids].sort((a, b) => getCatLabel(a).localeCompare(getCatLabel(b), 'fr'));
  }, [catalogue]);

  // --- Filtrage ---
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return catalogue.filter(r => {
      const matchSearch = !term ||
        (r.nom || '').toLowerCase().includes(term) ||
        (r.description || '').toLowerCase().includes(term);
      const matchCats = selectedCats.length === 0 ||
        selectedCats.every(c => (r.categories || []).includes(c));
      return matchSearch && matchCats;
    });
  }, [catalogue, search, selectedCats]);

  // --- Import ---
  const handleImport = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !selected || importing) return;
    if (userImports.has(selected.id)) return;

    setImporting(true);
    try {
      // 1. Copie dans la collection personnelle
      const userRecipeRef = await addDoc(collection(db, 'recettesUtilisateur', uid, 'recettes'), {
        nom:               selected.nom || '',
        description:       selected.description || '',
        tempsPreparation:  selected.tempsPreparation || 0,
        tempsCuisson:      selected.tempsCuisson || 0,
        nombrePersonnes:   selected.nombrePersonnes || 4,
        difficulte:        selected.difficulte || null,
        ingredients:       selected.ingredients || [],
        etapesPreparation: selected.etapesPreparation || [],
        categories:        [],
        source:            selected.source || '',
        auteur:            'catalogue',
        statut:            'publiee',
        note:              0,
        dateCreation:      serverTimestamp(),
        dateMiseAJour:     serverTimestamp(),
      });

      // 2. Tracking import — on stocke userRecipeId pour détecter les suppressions ultérieures
      await setDoc(
        doc(db, 'catalogueImports', uid, 'imports', selected.id),
        { importedAt: serverTimestamp(), userRecipeId: userRecipeRef.id }
      );

      // 3. Compteur
      await setDoc(
        doc(db, 'catalogueStats', selected.id),
        { downloadCount: increment(1) },
        { merge: true }
      );

      // Mise à jour locale
      setUserImports(prev => new Set([...prev, selected.id]));
      setStats(prev => ({ ...prev, [selected.id]: (prev[selected.id] || 0) + 1 }));
      setSelected(prev => ({ ...prev })); // force re-render du bouton
      setImportMsg({ text: 'Ajoutée à votre collection !', type: 'success' });
      setTimeout(() => setImportMsg(null), 3000);
    } catch (err) {
      console.error('Erreur import:', err);
      setImportMsg({ text: 'Erreur lors de l\'import. Réessayez.', type: 'error' });
      setTimeout(() => setImportMsg(null), 3000);
    } finally {
      setImporting(false);
    }
  };

  const closeDetail = () => { setSelected(null); setImportMsg(null); };
  const toggleCat = (id) =>
    setSelectedCats(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);

  // Fermer le détail avec Escape
  useEffect(() => {
    if (!selected) return;
    const handleKey = e => { if (e.key === 'Escape') closeDetail(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Render ---
  return (
    <div className="catalogue-page">
      <PageHeader
        title="Catalogue"
        onBack={() => navigate(-1)}
        backAriaLabel="Retour"
        sticky
      >
        {/* Recherche */}
        <div className="cat-search-wrapper">
          <svg className="cat-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="cat-search-input"
            placeholder="Rechercher une recette…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="cat-search-clear" onClick={() => setSearch('')} aria-label="Effacer">×</button>
          )}
        </div>

        {/* Filtres catégories */}
        {availableCats.length > 0 && (
          <div className="cat-chips-row">
            <button
              className={`cat-chip ${selectedCats.length === 0 ? 'cat-chip-active' : ''}`}
              onClick={() => setSelectedCats([])}
            >
              Tout
            </button>
            {availableCats.map(id => (
              <button
                key={id}
                className={`cat-chip ${selectedCats.includes(id) ? 'cat-chip-active' : ''}`}
                onClick={() => toggleCat(id)}
              >
                {getCatLabel(id)}
              </button>
            ))}
          </div>
        )}
      </PageHeader>

      {/* Corps */}
      <div className="catalogue-body">
        {loading ? (
          <div className="cat-loading">Chargement du catalogue…</div>
        ) : catalogue.length === 0 ? (
          <div className="cat-empty">
            <div className="cat-empty-icon">📚</div>
            <p className="cat-empty-title">Le catalogue est vide</p>
            <p className="cat-empty-sub">Les recettes apparaîtront ici une fois synchronisées depuis Recipe Studio.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="cat-empty">
            <p className="cat-empty-title">Aucun résultat</p>
            <button className="cat-reset-btn" onClick={() => { setSearch(''); setSelectedCats([]); }}>
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <>
            <p className="cat-count">{filtered.length} recette{filtered.length > 1 ? 's' : ''}</p>
            <div className="cat-list">
              {filtered.map(recipe => {
                const imported = userImports.has(recipe.id);
                const total = (recipe.tempsPreparation || 0) + (recipe.tempsCuisson || 0);
                const count = stats[recipe.id] || 0;
                const mainCat = (recipe.categories || [])[0];

                return (
                  <button
                    key={recipe.id}
                    className="cat-card"
                    onClick={() => setSelected(recipe)}
                  >
                    <div className="cat-card-main">
                      <div className="cat-card-info">
                        <div className="cat-card-title-row">
                          <h3 className="cat-card-title">{recipe.nom}</h3>
                          {imported && <span className="cat-badge-imported">✓ Ma collection</span>}
                        </div>
                        <div className="cat-card-meta">
                          {mainCat && getCatLabel(mainCat) && <span className="cat-meta-cat">{getCatLabel(mainCat)}</span>}
                          {total > 0 && <span className="cat-meta-item">⏱ {formatTime(total)}</span>}
                          {recipe.difficulte && <span className="cat-meta-item">{recipe.difficulte}</span>}
                          {count > 0 && <span className="cat-meta-item cat-meta-count">↓ {count}</span>}
                        </div>
                      </div>
                      <svg className="cat-card-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* === PANNEAU DÉTAIL === */}
      {selected && (
        <>
          <div className="cat-overlay" onClick={closeDetail} aria-hidden="true" />
          <div className="cat-detail-panel" role="dialog" aria-modal="true" aria-label={selected.nom}>

            {/* En-tête du panneau */}
            <div className="cat-detail-header">
              <div className="cat-detail-handle" />
              <button className="cat-detail-close" onClick={closeDetail} aria-label="Fermer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="cat-detail-body">
              <h2 className="cat-detail-title">{selected.nom}</h2>

              {/* Meta */}
              <div className="cat-detail-meta">
                {(selected.tempsPreparation || 0) + (selected.tempsCuisson || 0) > 0 && (
                  <div className="cat-detail-meta-item">
                    <span className="cat-detail-meta-label">Temps</span>
                    <span className="cat-detail-meta-value">
                      {formatTime((selected.tempsPreparation || 0) + (selected.tempsCuisson || 0))}
                    </span>
                  </div>
                )}
                {selected.difficulte && (
                  <div className="cat-detail-meta-item">
                    <span className="cat-detail-meta-label">Difficulté</span>
                    <span className="cat-detail-meta-value">{selected.difficulte}</span>
                  </div>
                )}
                {selected.nombrePersonnes > 0 && (
                  <div className="cat-detail-meta-item">
                    <span className="cat-detail-meta-label">Pour</span>
                    <span className="cat-detail-meta-value">{selected.nombrePersonnes} pers.</span>
                  </div>
                )}
                {(stats[selected.id] || 0) > 0 && (
                  <div className="cat-detail-meta-item">
                    <span className="cat-detail-meta-label">Importée</span>
                    <span className="cat-detail-meta-value">↓ {stats[selected.id]} fois</span>
                  </div>
                )}
              </div>

              {/* Catégories */}
              {(selected.categories || []).some(id => getCatLabel(id)) && (
                <div className="cat-detail-cats">
                  {selected.categories.filter(id => getCatLabel(id)).map(id => (
                    <span key={id} className="cat-detail-cat-tag">{getCatLabel(id)}</span>
                  ))}
                </div>
              )}

              {/* Description */}
              {selected.description && (
                <p className="cat-detail-desc">{selected.description}</p>
              )}

              {/* Ingrédients */}
              {(selected.ingredients || []).length > 0 && (
                <section className="cat-detail-section">
                  <h3 className="cat-detail-section-title">
                    Ingrédients ({selected.nombrePersonnes || 4} personnes)
                  </h3>
                  <ul className="cat-detail-ingredients">
                    {selected.ingredients.map((ing, i) => (
                      <li key={i} className="cat-ing-row">
                        <span className="cat-ing-qty">
                          {ing.quantite}{ing.unite ? ` ${ing.unite}` : ''}
                        </span>
                        <span className="cat-ing-nom">{ing.nom}</span>
                        {ing.commentaire && <span className="cat-ing-comment">({ing.commentaire})</span>}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Étapes */}
              {(selected.etapesPreparation || []).length > 0 && (
                <section className="cat-detail-section">
                  <h3 className="cat-detail-section-title">Préparation</h3>
                  <ol className="cat-detail-steps">
                    {selected.etapesPreparation.map((step, i) => (
                      <li key={i} className="cat-step-row">
                        <span className="cat-step-num">{i + 1}</span>
                        <span className="cat-step-desc">{step.description}</span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {/* Source */}
              {selected.source && (
                <p className="cat-detail-source">
                  Source : <span>{selected.source}</span>
                </p>
              )}
            </div>

            {/* Pied fixe — bouton import */}
            <div className="cat-detail-footer">
              {importMsg && (
                <div className={`cat-import-msg cat-import-msg-${importMsg.type}`}>
                  {importMsg.text}
                </div>
              )}
              {userImports.has(selected.id) ? (
                <div className="cat-already-imported">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Déjà dans votre collection
                </div>
              ) : (
                <button
                  className="cat-import-btn"
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? 'Ajout en cours…' : '+ Ajouter à ma collection'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
